// The page building blocks every Hiring screen uses, the same ones the other
// EquaOS apps draw (DPS-Employee-Management client/ui.jsx; the owner, 29 Oct
// 2026: "make it look exactly like the other systems"):
//
//   <Page>          the screen's column: header, then its blocks, evenly spaced
//   <PageHeader>    title, the line under it, and the screen's actions
//   <StatRow>/<Stat> the figures at the top, each a card, optionally a link
//   <Section>       a card with its heading (CardHeader) and its content
//   <Loading>, <Note>  waiting, and a plain message
//
// Built only from the design system (src/ds) and its CSS variables. A screen
// composes these and the design system's own Table, StatusPill, Button,
// Input, Select, Tabs, Modal and EmptyState; it never restyles them.

import React from 'react';
import { Card, CardHeader, useShellLayout } from '../../ds';

const useMobile = () => {
  const layout = useShellLayout();
  return Boolean(layout && layout.mobile);
};

export function Page({ children }) {
  const mobile = useMobile();
  // One column that never grows past the content area: a wide table scrolls
  // inside its own card rather than pushing the page out sideways.
  return <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: mobile ? 16 : 24, minWidth: 0 }}>{children}</div>;
}

/* A screen's heading. On a phone the top bar already shows the title, so
   only the line under it stays (still a heading for screen readers). */
export function PageHeader({ title, subtitle, actions }) {
  const mobile = useMobile();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: mobile ? 12 : 16, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: mobile ? 0 : 240 }}>
        <h1 style={mobile
          ? { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }
          : { fontFamily: 'var(--font-display)', fontSize: 'var(--fs-display-md)', fontWeight: 700, color: 'var(--text-1)', margin: 0 }}>{title}</h1>
        {subtitle && <p style={{ margin: mobile ? 0 : '4px 0 0', color: 'var(--text-2)', fontSize: mobile ? 14 : undefined }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

export function StatRow({ children }) {
  const mobile = useMobile();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${mobile ? 140 : 200}px,1fr))`, gap: mobile ? 12 : 20 }}>
      {children}
    </div>
  );
}

/* One figure. With onClick it is a link to where it is acted on; `tone`
   colours the figure for a figure that needs attention (warning / danger). */
export function Stat({ label, value, hint, onClick, tone, active = false }) {
  const color = tone === 'danger' ? 'var(--status-danger-text)' : tone === 'warning' ? 'var(--status-warning-text)' : 'var(--text-1)';
  const body = (
    <>
      <div style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>{label}</div>
      <div className="num" style={{ fontSize: 28, fontWeight: 600, marginTop: 6, color }}>{value}</div>
      {hint && <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)', marginTop: 2 }}>{hint}</div>}
    </>
  );
  if (!onClick) return <Card padding="18px 20px">{body}</Card>;
  return (
    <Card padding="18px 20px" role="button" tabIndex={0} aria-pressed={active || undefined}
      onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      style={{ cursor: 'pointer', ...(active ? { borderColor: 'var(--brand-primary)', boxShadow: '0 0 0 2px var(--brand-primary-soft)' } : {}) }}>
      {body}
    </Card>
  );
}

/* A card with its heading: the design system's Card and CardHeader. */
export function Section({ title, subtitle, action, padding, children }) {
  /* A card whose table runs edge to edge (padding "20px 0 8px") still keeps
     its heading off the card's edge (owner, 30 Oct 2026: "text touching the
     lines"). */
  const flush = typeof padding === 'string' && padding.trim().split(/\s+/)[1] === '0';
  const header = title && <CardHeader title={title} subtitle={subtitle} action={action} />;
  return (
    <Card padding={padding}>
      {header && (flush ? <div style={{ padding: '0 24px' }}>{header}</div> : header)}}
      {/* A table wider than the card scrolls inside it, never the page. */}
      <div style={{ overflowX: 'auto', minWidth: 0 }}>{children}</div>
    </Card>
  );
}

export function Loading({ label = 'Loading' }) {
  return (
    <div role="status" style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
      {label}…
    </div>
  );
}

/* A plain message in the page: info, warning or danger. */
export function Note({ tone = 'info', children }) {
  const bg = { info: 'var(--status-info-bg)', warning: 'var(--status-warning-bg)', danger: 'var(--status-danger-bg)', success: 'var(--status-success-bg)' }[tone];
  const fg = { info: 'var(--status-info-text)', warning: 'var(--status-warning-text)', danger: 'var(--status-danger-text)', success: 'var(--status-success-text)' }[tone];
  return <div role={tone === 'danger' ? 'alert' : 'status'} style={{ background: bg, color: fg, borderRadius: 'var(--radius-md)', padding: '12px 16px', fontSize: 'var(--fs-body-sm)' }}>{children}</div>;
}
